import {
  useCreateMutation,
  useGetQuery,
  WorkspaceRoutine,
  RoutineType,
  useEmbeddedRoutines,
} from '@gripp/shared-logic';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../loadingIndicator';
import { AlertForm } from './alertForm';

const GET_PRODUCT_WITH_ROUTINES = `
  query GetProduct($id: ID!) {
    getProduct(id: $id) {
      id
      name
      description
      workspace {
        id
        name
      }
      routines {
        id
        name
        type
        order
        config
      }
    }
  }
`;

const CREATE_WORKFLOW = `
  mutation CreateWorkflow($input: CreateWorkflowInput!) {
    workflow: createWorkflow(input: $input) {
      id
      name
    }
  }
`;

type ProductAlertFormProps = {
  productId: string;
  onClose: () => void;
};

export const ProductAlertForm = ({
  productId,
  onClose,
}: ProductAlertFormProps) => {
  const { t } = useTranslation();

  const { data: productData, isLoading } = useGetQuery({
    modelName: 'product',
    query: GET_PRODUCT_WITH_ROUTINES,
    id: productId,
  });

  const [defaultObject, setDefaultObject] = useState<any>(null);
  const [counterRoutines, setCounterRoutines] = useState<WorkspaceRoutine[]>([]);
  const [availableRoutines, setAvailableRoutines] = useState<WorkspaceRoutine[]>([]);
  const [currentProduct, setCurrentProduct] = useState<any>(null);

  const directRoutines = (productData as any)?.routines || [];
  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } = useEmbeddedRoutines(directRoutines);

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  useEffect(() => {
    if (!isLoading && productData) {
      const product = productData as any;
      
      setCurrentProduct(product);
      
      const counterRoutines = routinesWithEmbedded.filter(
        (routine: any) => routine.type === RoutineType.Counter
      );
      setCounterRoutines(counterRoutines);
      setAvailableRoutines(routinesWithEmbedded);

      const productName = product.name;
      const daysStartDate = moment().format('MM/DD/YYYY');

      const defaultValues = {
        alertLabel: '',
        alertLabelPlaceholder: `${t('alerts.alert')}: ${productName}`,
        startDate: undefined,
        endDate: undefined,
        minValue: undefined,
        maxValue: undefined,
        alertType: undefined,
        secondAlertType: undefined,
        days: undefined,
        daysStartDate: daysStartDate,
        completions: undefined,
        daysSkipped: undefined,
        onDate: undefined,
        intervalRoutineId: undefined,
        onInterval: undefined,
        onIntervalStart: undefined,
      };

      setDefaultObject(defaultValues);
    }
  }, [isLoading, isEmbeddedRoutinesLoading, productData, routinesWithEmbedded, t]);

  const submitAlert = async (input: any) => {
    await createWorkflow(
      { input },
      {
        onSuccess: async () => {
          onClose();
        },
      }
    );
  };

  return isLoading || !defaultObject || !currentProduct ? (
    <LoadingIndicator />
  ) : (
    <AlertForm
      defaultValues={defaultObject}
      counterRoutines={counterRoutines}
      product={currentProduct}
      availableRoutines={availableRoutines}
      submitAlert={submitAlert}
      onClose={onClose}
      headerTitle="alerts.newAlert"
      backButtonLabel={t('topNav.back')}
      openDropdownOnLoad={false}
    />
  );
};